import { type ReactNode } from 'react';
import { DialogBody, DialogFooter, DialogHeader, type DialogHeaderProps, DialogTitle, ResponsiveDialog, type ResponsiveDialogProps } from './dialog';
export interface DefaultDialogProps extends ResponsiveDialogProps {
  header?: Omit<DialogHeaderProps, 'onClose'>;
  body?: ReactNode;
  footer?: ReactNode;
}

/**
 * @example
 * overlay.open(({ unmount }) => (
 *  <DefaultDialog
 *    onClose={unmount}
 *    size="xs"
 *    className="h-[500px]"
 *    header={{
 *      title: 'Dialog Header',
 *      hasCloseButton: true,
 *      topSlot: '👋',
 *      leftSlot: (
 *        <IconButton variant="transparent" intent="neutral" size="sm">
 *          <ChevronLeftIcon size="md" />
 *        </IconButton>
 *      ),
 *      rightSlot: (
 *        <IconButton variant="transparent" intent="neutral" size="sm">
 *          <MoreIcon size="md" />
 *        </IconButton>
 *      ),
 *    }}
 *    body={<div>Body</div>}
 *    footer={<div>Footer</div>}
 *    hasDim={false}
 *    shouldCloseOnDimClick={false}
 *  />
 * ));
 */
export const DefaultDialog = ({
  onClose,
  size,
  className,
  header,
  body,
  footer,
  hasDrawerHandle,
  hasDim,
  shouldCloseOnDimClick,
  // DefaultDialogProps 는 ResponsiveDialogProps 를 상속하는데 이 프롭만 전달이 빠져 있어
  // 넘겨도 조용히 무시됐다(size 가 lg/xl/2xl 이 아니면 모바일에서도 drawer 가 안 됨).
  forceDrawerOnMobile
}: DefaultDialogProps) => {
  const hasBody = Boolean(body);
  return <ResponsiveDialog onClose={onClose} size={size} className={className} hasDrawerHandle={hasDrawerHandle} hasDim={hasDim} shouldCloseOnDimClick={shouldCloseOnDimClick} forceDrawerOnMobile={forceDrawerOnMobile} data-sentry-element="ResponsiveDialog" data-sentry-component="DefaultDialog" data-sentry-source-file="DefaultDialog.tsx">
      {/* header 없는 alert/confirm 은 본문 메시지를 접근성 이름으로 쓴다(Radix Title 요구). */}
      {!header && !hasBody && <DialogTitle className="sr-only">Dialog</DialogTitle>}
      {header && <DialogHeader className={header.className} title={header.title} ariaTitle={header.ariaTitle} hasCloseButton={header.hasCloseButton} topSlot={header.topSlot} leftSlot={header.leftSlot} rightSlot={header.rightSlot} onClose={onClose} />}
      {hasBody && (header ? <DialogBody>{body}</DialogBody> : <DialogTitle asChild>
            <DialogBody>{body}</DialogBody>
          </DialogTitle>)}
      {Boolean(footer) && <DialogFooter>{footer}</DialogFooter>}
    </ResponsiveDialog>;
};